import { useAuth0 } from "@auth0/auth0-react";
import { Language, Logout, Person, Settings } from "@mui/icons-material";
import {
  Avatar,
  Chip,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Toolbar from "@mui/material/Toolbar";
import { useTheme } from "@mui/material/styles";
import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import TALogo from "../logo/TALogo.svg";
// import i18n from "../tools/i18n";
import TranslateIcon from "@mui/icons-material/Translate";
import { useQueryClient } from "@tanstack/react-query";
import { Profile } from "../types/contact";
import MobileMenu from "./MenuMobile";

interface Props {
  setAppBarHeight?: Dispatch<SetStateAction<number>>;
}

interface Lang {
  code: string;
  nativeName: string;
}

const langs: Lang[] = [
  { code: "en", nativeName: "English" },
  { code: "cy", nativeName: "Welsh" },
];

const TeacherActiveAppBar: FunctionComponent<Props> = (
  props: Props
): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { setAppBarHeight } = props;

  const { isAuthenticated, loginWithRedirect, logout, user, isLoading } =
    useAuth0();
  const { data: profile } = useApi<Profile>("profile");
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [languageOpen, setLanguageOpen] = useState<null | HTMLElement>(null);

  const [currentLang, setCurrentLang] = useState<Lang>(
    langs.find((l) => l.code.toLowerCase().startsWith(i18n.language)) ??
      langs[0]
  );

  const queryClient = useQueryClient();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const location = useLocation();
  const navigate = useNavigate();

  const appBarRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (appBarRef.current && setAppBarHeight) {
      setAppBarHeight(appBarRef.current.clientHeight);
    }
  }, [isSmallScreen, setAppBarHeight]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const toggleLanguage = () => {
    const lang = currentLang.code === langs[0].code ? langs[1] : langs[0];
    setCurrentLang(lang);
    i18n.changeLanguage(lang.code).then(() => {
      queryClient.invalidateQueries({ queryKey: ["faq"] });
    });
  };

  const changeLanguage = (lang: Lang) => {
    setCurrentLang(lang);
    i18n.changeLanguage(lang.code).then(() => {
      queryClient.invalidateQueries({ queryKey: ["faq"] });
    });
  };

  const openLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageOpen(event.currentTarget);
  };

  const closeLanguageMenu = () => {
    setLanguageOpen(null);
  };

  const pageName =
    location.pathname === "/"
      ? t("current.outstanding2")
      : location.pathname === "/history"
      ? t("history.title")
      : location.pathname === "/contacts"
      ? t("contacts.title")
      : location.pathname === "/faq"
      ? t("FAQ.title")
      : location.pathname === "/recentlyapproved"
      ? t("current.recentlyApproved")
      : location.pathname === "/settings" && location.search === "?tab=2"
      ? t("clientPage.title")
      : location.pathname === "/settings" && location.search === "?tab=3"
      ? t("adminPage.title")
      : location.pathname === "/settings" ||
        (location.pathname === "/settings" && location.search === "?tab=1")
      ? t("profilePage.title")
      : location.pathname === "/login"
      ? "Login"
      : "Not Found";

  return (
    <AppBar
      ref={appBarRef}
      position={isSmallScreen ? "fixed" : "static"}
      sx={{
        pt: "15px",
        pb: "15px",
        pl: isSmallScreen ? "15px" : 0,
        pr: isSmallScreen ? "15px" : 0,
        "--appBarHeight": "auto",
      }}
    >
      <Container fixed disableGutters>
        <Toolbar disableGutters>
          {isSmallScreen ? (
            <Stack
              direction={"row"}
              spacing={2}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ width: "100%" }}
            >
              <Stack
                direction={"row"}
                spacing={2}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                {!isAuthenticated && !isLoading ? (
                  <Button
                    variant="text"
                    sx={{ width: "fit-content" }}
                    onClick={() => loginWithRedirect()}
                  >
                    {t("login.loginButton")}
                  </Button>
                ) : (
                  <>
                    <MobileMenu />
                    <Typography variant="h6"> {pageName} </Typography>
                  </>
                )}
              </Stack>

              <Chip
                label={currentLang.nativeName}
                onClick={openLanguageMenu}
                icon={<TranslateIcon fontSize="small" />}
                variant="outlined"
                color="primary"
              />

              <Menu
                anchorEl={languageOpen}
                open={Boolean(languageOpen)}
                onClose={closeLanguageMenu}
                onClick={closeLanguageMenu}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      overflow: "visible",
                      filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                      mt: 1.5,
                      "& .MuiAvatar-root": {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                      "&::before": {
                        content: '""',
                        display: "block",
                        position: "absolute",
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: "background.paper",
                        transform: "translateY(-50%) rotate(45deg)",
                        zIndex: 0,
                      },
                    },
                  },
                }}
                transformOrigin={{
                  horizontal: "right",
                  vertical: "top",
                }}
                anchorOrigin={{
                  horizontal: "right",
                  vertical: "bottom",
                }}
              >
                {langs.map((lang, index) => (
                  <MenuItem key={index} onClick={() => changeLanguage(lang)}>
                    <Typography> {lang.nativeName} </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          ) : (
            <>
              {isSmallScreen && <MobileMenu />}
              <Box
                sx={{ flexGrow: 1 }}
                display={"flex"}
                alignContent={"center"}
              >
                <Box
                  component={"img"}
                  src={TALogo}
                  alt="Logo"
                  sx={{ width: isMediumScreen ? "40%" : "auto" }}
                />
              </Box>

              <Box sx={{ flexGrow: 0 }}>
                <Stack direction={"row"} alignItems={"center"}>
                  {isAuthenticated && !isLoading && (
                    <>
                      <Typography variant="body1" sx={{ marginRight: "20px" }}>
                        {/* {clientQuery?.results?.name} */}
                        {profile?.results?.name}
                      </Typography>

                      <Box sx={{ flexGrow: 0 }}>
                        <Tooltip title={t("tooltips.openSettings")}>
                          <IconButton
                            onClick={handleOpenUserMenu}
                            sx={{ p: 0 }}
                          >
                            <Avatar
                              variant="rounded"
                              sx={{
                                backgroundColor: "rgba(0,82,146, 0.4)",
                                color: "white",
                              }}
                            >
                              {profile?.results?.name.charAt(0).toUpperCase()}{" "}
                            </Avatar>
                          </IconButton>
                        </Tooltip>

                        <Menu
                          anchorEl={anchorElUser}
                          id="account-menu"
                          open={Boolean(anchorElUser)}
                          onClose={handleCloseUserMenu}
                          onClick={handleCloseUserMenu}
                          slotProps={{
                            paper: {
                              elevation: 0,
                              sx: {
                                overflow: "visible",
                                filter:
                                  "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                                mt: 1.5,
                                "& .MuiAvatar-root": {
                                  width: 32,
                                  height: 32,
                                  ml: -0.5,
                                  mr: 1,
                                },
                                "&::before": {
                                  content: '""',
                                  display: "block",
                                  position: "absolute",
                                  top: 0,
                                  right: 14,
                                  width: 10,
                                  height: 10,
                                  bgcolor: "background.paper",
                                  transform: "translateY(-50%) rotate(45deg)",
                                  zIndex: 0,
                                },
                              },
                            },
                          }}
                          transformOrigin={{
                            horizontal: "right",
                            vertical: "top",
                          }}
                          anchorOrigin={{
                            horizontal: "right",
                            vertical: "bottom",
                          }}
                        >
                          <MenuItem onClick={() => navigate("/settings")}>
                            <ListItemIcon>
                              <Person fontSize="small" />
                            </ListItemIcon>
                            {profile?.results?.name} <br />
                            {user?.email}
                          </MenuItem>

                          <Divider />

                          <MenuItem onClick={() => navigate("/settings")}>
                            <ListItemIcon>
                              <Settings fontSize="small" />
                            </ListItemIcon>
                            {t("settings.title")}
                          </MenuItem>

                          <MenuItem onClick={() => toggleLanguage()}>
                            <ListItemIcon>
                              <Language fontSize="small" />
                            </ListItemIcon>
                            {`${t("layout.changeLanguage")} (${
                              currentLang.nativeName
                            })`}
                          </MenuItem>

                          <Divider />

                          <MenuItem
                            onClick={() =>
                              logout({
                                logoutParams: {
                                  returnTo: window.location.origin + "/login",
                                },
                              })
                            }
                          >
                            <ListItemIcon>
                              <Logout fontSize="small" />
                            </ListItemIcon>
                            {t("logout.title")}
                          </MenuItem>
                        </Menu>
                      </Box>
                    </>
                  )}

                  {!isAuthenticated && !isLoading && (
                    <Box sx={{ flexGrow: 0 }}>
                      <Button
                        color="inherit"
                        onClick={() => loginWithRedirect()}
                      >
                        {t("login.loginButton")}
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Box>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default TeacherActiveAppBar;
